import { createContext, useContext, type ReactNode } from 'react'

import type {
  AnyDataGridContract,
  ColumnId,
  ContractValues,
  DataGridColumnRenderers,
  DataGridItem,
} from '../lib/types'
import type { useDataGrid } from '../lib/use-data-grid'

type DataGridInstance<Contract extends AnyDataGridContract> = ReturnType<
  typeof useDataGrid<Contract>
>

export type DataGridProps<Contract extends AnyDataGridContract> = {
  grid: DataGridInstance<Contract>
  caption: string
  regionLabel?: string
  noun?: string
  singularNoun?: string
  searchLabel?: string
  assistantFilter?: string
  renderers?: DataGridColumnRenderers<Contract>
  emptyMessage?: ReactNode
  onRowOpen?: (row: DataGridItem<ContractValues<Contract>>) => void
  /** The accessible name of a row when `onRowOpen` is set. Defaults to "View <name>". */
  rowActionLabel?: (row: DataGridItem<ContractValues<Contract>>) => string
  /** Rows to show as selected, by key. */
  selectedRowKeys?: readonly string[]
  /** Lets rows expand into a detail row below them. The page owns which rows are open. */
  rowDetail?: {
    hasDetail: (row: DataGridItem<ContractValues<Contract>>) => boolean
    isExpanded: (row: DataGridItem<ContractValues<Contract>>) => boolean
    onToggle: (row: DataGridItem<ContractValues<Contract>>) => void
    render: (row: DataGridItem<ContractValues<Contract>>) => ReactNode
    label: (row: DataGridItem<ContractValues<Contract>>) => string
  }
  toolbarActions?: ReactNode
  menuActions?: ReactNode
  /** Shows the user's saved views as tabs above the toolbar, beside the views in the table options menu. */
  savedViewTabs?: boolean
  columnChooserColumns?: readonly ColumnId<Contract>[]
  additionalColumns?: readonly {
    id: string
    label: string
    cell: (row: DataGridItem<ContractValues<Contract>>) => ReactNode
  }[]
  children?: ReactNode
}

export const DataGridContext = createContext<DataGridProps<AnyDataGridContract> | null>(null)

export function useGridContext() {
  const value = useContext(DataGridContext)
  if (!value) throw new Error('DataGrid compound components require DataGrid.Root')
  return value
}
